export const PROVIDER_IDS = [
  'openai-gpt-image-2',
  'google-gemini-3',
  'aixoras-gpt-image-2',
  'frimodel-gpt-image-2',
  'apib-gpt-image-2',
  'apib-gpt-image-2-official',
  'apib-gpt-image-2-5-ext-flare',
  'apib-gpt-image-2-5-ext-sunburst',
  'apib-seedream-5-0',
  'apib-seedream-5-0-lite',
  'apib-grok-imagine-2-0-ext',
  'pianyitoken-gpt-image-2',
] as const;

export type ProviderType = (typeof PROVIDER_IDS)[number];

export type ProviderMetadata = {
  label: string;
  badgeLabel: string;
  badgeClassName: string;
  modeSupport: {
    text2img: boolean;
    img2img: boolean;
    inpaint: boolean;
  };
  defaultReferenceImageMaxCount?: number;
  maxReferenceImageMaxCount?: number;
  maxOutputImageCount?: number;
  supportedAspectRatios?: string[];
  multiReferenceInpaint?: boolean;
  adminCapability: string;
  adminDetail: string;
};

export const ALL_IMAGE_ASPECT_RATIOS = [
  'auto',
  '1:1',
  '4:3',
  '3:4',
  '3:2',
  '2:3',
  '16:9',
  '9:16',
  '4:5',
  '5:4',
  '2:1',
  '1:2',
  '3:1',
  '1:3',
  '21:9',
  '9:21',
] as const;

export const PROVIDER_METADATA: Record<ProviderType, ProviderMetadata> = {
  'openai-gpt-image-2': {
    label: 'OpenAI gpt-image-2 同步协议',
    badgeLabel: 'openai-gpt-image-2',
    badgeClassName: 'bg-blue-100 text-blue-800',
    modeSupport: { text2img: true, img2img: true, inpaint: true },
    defaultReferenceImageMaxCount: 16,
    maxReferenceImageMaxCount: 16,
    adminCapability: '严格按 OpenAI 官方规范：图生图最多 16 张参考图；支持局部重绘',
    adminDetail:
      '完全按 gpt-image-1/1.5/2 官方协议：文生图走 /images/generations，图生图/局部重绘走 multipart /images/edits，参考图必须以文件形式上传，mask 必须为 PNG',
  },
  'google-gemini-3': {
    label: 'Google Gemini 3 图片生成',
    badgeLabel: 'google-gemini-3',
    badgeClassName: 'bg-green-100 text-green-700',
    modeSupport: { text2img: true, img2img: true, inpaint: true },
    defaultReferenceImageMaxCount: 4,
    maxReferenceImageMaxCount: 4,
    supportedAspectRatios: [
      '1:1',
      '1:4',
      '1:8',
      '2:3',
      '3:2',
      '3:4',
      '4:1',
      '4:3',
      '4:5',
      '5:4',
      '8:1',
      '9:16',
      '16:9',
      '21:9',
    ],
    adminCapability: '图生图默认最多 4 张参考图；支持局部重绘',
    adminDetail: '使用 /v1beta/models/{model}:generateContent，文生图和图生图都走 inline_data 结构',
  },
  'aixoras-gpt-image-2': {
    label: 'Aixoras gpt-image-2 异步轮询',
    badgeLabel: 'aixoras-gpt-image-2',
    badgeClassName: 'bg-slate-100 text-slate-700',
    modeSupport: { text2img: true, img2img: true, inpaint: true },
    defaultReferenceImageMaxCount: 16,
    maxReferenceImageMaxCount: 16,
    supportedAspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '21:9'],
    multiReferenceInpaint: true,
    adminCapability: '文生图、图生图与局部重绘统一走异步任务轮询；图生图与局部重绘最多支持 16 张参考图',
    adminDetail:
      '文生图走 /v1/images/generations/async，图生图与局部重绘走 /v1/images/edits/async，支持 multipart image/image[] 与 JSON reference_images，完成态通过 /v1/images/tasks/{task_id} 轮询获取结果',
  },
  'frimodel-gpt-image-2': {
    label: 'Frimodel gpt-image-2 同步协议',
    badgeLabel: 'frimodel-gpt-image-2',
    badgeClassName: 'bg-violet-100 text-violet-700',
    modeSupport: { text2img: true, img2img: true, inpaint: true },
    adminCapability: '文生图走最小参数集；图生图与局部重绘统一走 multipart image[] 上传',
    adminDetail:
      '文生图走 /v1/images/generations，仅发送 model/prompt/size/quality/output_format；图生图与局部重绘都走 multipart /v1/images/edits（image[] 上传），局部重绘额外追加 OpenAI 风格的 mask 字段',
  },
  'apib-gpt-image-2': {
    label: 'apib.ai gpt-image-2 异步轮询（基础版）',
    badgeLabel: 'apib-gpt-image-2',
    badgeClassName: 'bg-sky-100 text-sky-700',
    modeSupport: { text2img: true, img2img: true, inpaint: false },
    defaultReferenceImageMaxCount: 16,
    maxReferenceImageMaxCount: 16,
    adminCapability: '图生图默认最多 16 张参考图；不支持局部重绘',
    adminDetail: '使用 /images/generations + /tasks/{task_id} 轮询，支持 URL 与 base64 混合参考图',
  },
  'apib-gpt-image-2-official': {
    label: 'apib.ai gpt-image-2-official 异步轮询（完整版）',
    badgeLabel: 'apib-gpt-image-2-official',
    badgeClassName: 'bg-cyan-100 text-cyan-700',
    modeSupport: { text2img: true, img2img: true, inpaint: true },
    defaultReferenceImageMaxCount: 16,
    maxReferenceImageMaxCount: 16,
    adminCapability: '图生图默认最多 16 张参考图；支持局部重绘（mask_url）',
    adminDetail: '异步任务接口，内部自动轮询 task_id，并把 low/medium/high 映射为 1K/2K/4K',
  },
  'apib-gpt-image-2-5-ext-flare': {
    label: 'apib.ai GPT Image 2.5 Ext Flare（按张计费）',
    badgeLabel: 'apib-2.5-ext-flare',
    badgeClassName: 'bg-sky-100 text-sky-700',
    modeSupport: { text2img: true, img2img: true, inpaint: false },
    defaultReferenceImageMaxCount: 16,
    maxReferenceImageMaxCount: 16,
    maxOutputImageCount: 1,
    supportedAspectRatios: ['auto', '1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '5:4', '4:5', '21:9'],
    adminCapability: '图生图默认最多 16 张参考图；1K/2K/4K 按张计费；不支持局部重绘',
    adminDetail: '使用 /images/generations + /tasks/{id} 轮询；model=gpt-image-2.5-ext，version=flare；size 传比例，resolution 传 1K/2K/4K',
  },
  'apib-gpt-image-2-5-ext-sunburst': {
    label: 'apib.ai GPT Image 2.5 Ext Sunburst（按张计费）',
    badgeLabel: 'apib-2.5-ext-sunburst',
    badgeClassName: 'bg-amber-100 text-amber-700',
    modeSupport: { text2img: true, img2img: true, inpaint: false },
    defaultReferenceImageMaxCount: 16,
    maxReferenceImageMaxCount: 16,
    maxOutputImageCount: 1,
    supportedAspectRatios: ['auto', '1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '5:4', '4:5', '21:9'],
    adminCapability: '图生图默认最多 16 张参考图；1K/2K/4K 按张计费；不支持局部重绘',
    adminDetail: '使用 /images/generations + /tasks/{id} 轮询；model=gpt-image-2.5-ext，version=sunburst；size 传比例，resolution 传 1K/2K/4K',
  },
  'apib-seedream-5-0': {
    label: 'apib.ai Seedream 5.0 Pro',
    badgeLabel: 'apib-seedream-5-0',
    badgeClassName: 'bg-fuchsia-100 text-fuchsia-700',
    modeSupport: { text2img: true, img2img: true, inpaint: false },
    defaultReferenceImageMaxCount: 10,
    maxReferenceImageMaxCount: 10,
    maxOutputImageCount: 1,
    supportedAspectRatios: ['auto', '1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '21:9'],
    adminCapability: '图生图默认最多 10 张参考图；异步任务轮询；不支持局部重绘',
    adminDetail: '使用 /images/generations + /tasks/{task_id} 轮询，比例走 aspectRatio，quality 映射 1K/2K',
  },
  'apib-seedream-5-0-lite': {
    label: 'apib.ai Seedream 5.0 Lite',
    badgeLabel: 'apib-seedream-5-0-lite',
    badgeClassName: 'bg-pink-100 text-pink-700',
    modeSupport: { text2img: true, img2img: true, inpaint: false },
    defaultReferenceImageMaxCount: 10,
    maxReferenceImageMaxCount: 10,
    maxOutputImageCount: 15,
    supportedAspectRatios: ['auto', '1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '2:1', '1:2', '21:9'],
    adminCapability: '图生图默认最多 10 张参考图；单次最多 15 张；支持 2K/3K/4K 分辨率；不支持局部重绘',
    adminDetail: '使用 /images/generations + /tasks/{task_id} 轮询，Lite 版仅支持 2K/3K/4K（不支持 1K），output_format 支持 jpeg/png',
  },
  'apib-grok-imagine-2-0-ext': {
    label: 'apib.ai Grok Imagine 2.0 Ext',
    badgeLabel: 'apib-grok-imagine-2-0-ext',
    badgeClassName: 'bg-amber-100 text-amber-700',
    modeSupport: { text2img: true, img2img: true, inpaint: true },
    defaultReferenceImageMaxCount: 1,
    maxReferenceImageMaxCount: 1,
    maxOutputImageCount: 12,
    supportedAspectRatios: ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'],
    adminCapability: '图生图仅支持 1 张参考图；支持区域编辑（segment + region_edit 两步流程）；单次最多 12 张',
    adminDetail: '使用 /images/generations + /tasks/{task_id} 轮询；区域编辑先 segment 获取图层 image_id 和 objects，再 region_edit 修改选区；resolution 固定 quality',
  },
  'pianyitoken-gpt-image-2': {
    label: 'pianyitoken media 图片（非 Pro）',
    badgeLabel: 'pianyitoken-media',
    badgeClassName: 'bg-emerald-100 text-emerald-700',
    modeSupport: { text2img: true, img2img: true, inpaint: false },
    defaultReferenceImageMaxCount: 6,
    maxReferenceImageMaxCount: 9,
    maxOutputImageCount: 10,
    adminCapability: '文生图 / 图生图；图生图最多 6 张参考图；不支持局部重绘',
    adminDetail: 'pianyitoken media async 接口：只发送 model/prompt/n/size（图生图额外上传 image），不发送 quality/moderation/output_format；成功返回 data[].url',
  },
};

export function getProviderMetadata(provider?: string | null): ProviderMetadata {
  const normalized = (provider || 'openai-gpt-image-2') as ProviderType;
  return PROVIDER_METADATA[normalized] || PROVIDER_METADATA['openai-gpt-image-2'];
}

export function getProviderDefaultReferenceImageMaxCount(provider?: string | null): number | undefined {
  return getProviderMetadata(provider).defaultReferenceImageMaxCount;
}

export function getProviderMaxReferenceImageMaxCount(provider?: string | null): number | undefined {
  return getProviderMetadata(provider).maxReferenceImageMaxCount;
}

export function getProviderMaxOutputImageCount(provider?: string | null): number | undefined {
  return getProviderMetadata(provider).maxOutputImageCount;
}

export function getProviderSupportedAspectRatios(provider?: string | null): string[] {
  return [...(getProviderMetadata(provider).supportedAspectRatios || ALL_IMAGE_ASPECT_RATIOS)];
}

export function assertProviderSupportsInpaint(provider?: string | null): void {
  const metadata = getProviderMetadata(provider);
  if (!metadata.modeSupport.inpaint) {
    throw new Error(`${metadata.label} 暂不支持局部重绘`);
  }
}

export function assertProviderReferenceImageCount(provider: string | null | undefined, count: number): void {
  const maxCount = getProviderMaxReferenceImageMaxCount(provider);
  if (maxCount && count > maxCount) {
    const metadata = getProviderMetadata(provider);
    throw new Error(`${metadata.label} 最多支持 ${maxCount} 张参考图`);
  }
}

export function assertProviderOutputImageCount(provider: string | null | undefined, count: number): void {
  const maxCount = getProviderMaxOutputImageCount(provider);
  if (maxCount && count > maxCount) {
    const metadata = getProviderMetadata(provider);
    throw new Error(`${metadata.label} 暂只支持 n=${maxCount}`);
  }
}
